// app/admin/_layout.tsx

import { checkCurrentUserIsAdmin } from "@/lib/admin/adminAuth";
import { auth } from "@/lib/firebase";
import { Redirect, Stack, usePathname } from "expo-router";
import { onAuthStateChanged } from "firebase/auth";
import React, { useEffect, useState } from "react";
import { ActivityIndicator, StyleSheet, Text, View } from "react-native";

type GuardState = "checking" | "allowed" | "denied";

export default function AdminLayout() {
  const pathname = usePathname();

  const isAdminSignInPage = pathname === "/admin/signin";

  const [guardState, setGuardState] = useState<GuardState>("checking");
  const [, setMessage] = useState("");

  useEffect(() => {
    if (isAdminSignInPage) {
      setGuardState("allowed");
      return;
    }

    setGuardState("checking");

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        if (!user) {
          setMessage("No admin user is signed in.");
          setGuardState("denied");
          return;
        }

        const adminCheck = await checkCurrentUserIsAdmin();

        if (!adminCheck.isAdmin) {
          setMessage(
            adminCheck.reason ??
              "This account is not allowed to access admin."
          );
          setGuardState("denied");
          return;
        }

        setMessage("");
        setGuardState("allowed");
      } catch (error) {
        console.warn("Admin access check failed.");

        setMessage(
          error instanceof Error
            ? error.message
            : "Unable to verify admin access."
        );

        setGuardState("denied");
      }
    });

    return unsubscribe;
  }, [isAdminSignInPage]);

  /**
   * Do not protect the sign-in page itself.
   * Otherwise /admin/signin would redirect to itself forever.
   */
  if (isAdminSignInPage) {
    return <Stack screenOptions={{ headerShown: false }} />;
  }

  if (guardState === "checking") {
    return (
      <View style={styles.center}>
        <ActivityIndicator />
        <Text style={styles.message}>Checking admin access...</Text>
      </View>
    );
  }

  if (guardState === "denied") {
    console.warn("Admin access denied.");
    return <Redirect href="/admin/signin" />;
  }

  return <Stack screenOptions={{ headerShown: false }} />;
}

const styles = StyleSheet.create({
  center: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    backgroundColor: "#ffffff",
  },

  message: {
    marginTop: 12,
    color: "#4b5563",
    fontWeight: "700",
  },
});
